/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarMedium,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import {
  AppstoreOutlined,
  CalendarOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  FileAddOutlined,
  PicCenterOutlined,
  FileSearchOutlined,
  MailOutlined,
  PoweroffOutlined,
  SafetyOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Badge, Button, Menu } from 'antd';

import css from './TopbarMobileMenu.module.css';
import { createResourceLocatorString, pathByRouteName } from '../../../../util/routes';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { useHistory } from 'react-router-dom';
import appSettings from '../../../../config/settings';
import { userTypeOf } from '../../../../util/userType';
import { teamRoleOf } from '../../../../util/teamRole';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount = 0,
    totalUnreadConversations = 0,
    customLinks,
    onLogout,
    isOnboardingForm = false,
    profileListing,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const isCandidate = userTypeOf(currentUser) === 'candidate';
  const isCustomer = userTypeOf(currentUser) === 'customer';

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <NamedLink
            name="SignupPage"
            to={{ state: { freeTrial: true } }}
            className={css.freeTrialLink}
          >
            <FormattedMessage id="TopbarMobileMenu.freeTrialLink" />
          </NamedLink>

          <div className={css.customLinksWrapper}>{extraLinks}</div>

          <div className={css.spacer} />
        </div>
        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };
  const inboxTab = currentUserHasListings ? 'sales' : 'orders';
  
  const [selectedTab, setSelectedTab] = useState(null);
  const [openTab, setOpenTab] = useState(null);
  const [openKeys, setOpenKeys] = useState([openTab]);
  
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();

  useEffect(() => {
    const currentPage = window.location.pathname.split('/')[
      window.location.pathname.split('/').length - 1
    ];

    switch (currentPage) {
      case 'dashboard':
        localStorage.setItem('selectedTab', 1);
        localStorage.setItem('openTab', 1);
        break;
      case 'wallet':
        localStorage.setItem('selectedTab', 13);
        localStorage.setItem('openTab', 13);
        break;
      case 'findJob':
        localStorage.setItem('selectedTab', 2);
        localStorage.setItem('openTab', 2);
        break;
      case 'postJob':
        localStorage.setItem('selectedTab', 3);
        localStorage.setItem('openTab', 3);
        break;
      case 'orders':
      case 'sales':
        localStorage.setItem('selectedTab', 4);
        localStorage.setItem('openTab', 4);
        break;
      case 'calendar':
        localStorage.setItem('selectedTab', 5);
        localStorage.setItem('openTab', 5);
        break;
      case 'user-manage':
        localStorage.setItem('selectedTab', 6);
        localStorage.setItem('openTab', 6);
        break;
      case 'verifications':
        localStorage.setItem('selectedTab', 7);
        localStorage.setItem('openTab', 7);
        break;
      case 'job-dashboard':
        localStorage.setItem('selectedTab', 11);
        localStorage.setItem('openTab', 11);
        break;
      case 'profile-settings':
        localStorage.setItem('selectedTab', 8);
        localStorage.setItem('openTab', 8);
        break;
      case 'contact-details':
        localStorage.setItem('selectedTab', '9a');
        localStorage.setItem('openTab', '9');
        break;
      case 'change-password':
        localStorage.setItem('selectedTab', '9b');
        localStorage.setItem('openTab', '9');
        break;
      case 'payments':
        localStorage.setItem('selectedTab', '9c');
        localStorage.setItem('openTab', '9');
        break;
      case 'payment-methods':
        localStorage.setItem('selectedTab', '9d');
        localStorage.setItem('openTab', '9');
        break;
      case 'twilio-consent':
        localStorage.setItem('selectedTab', '9e');
        localStorage.setItem('openTab', '9');
        break;
      case 'payments-history':
        localStorage.setItem('selectedTab', '9f');
        localStorage.setItem('openTab', '9');
        break;
        
      default:
        break;
    }

    const storedTab = localStorage.getItem('selectedTab');
    if (storedTab) {
      setSelectedTab(storedTab);

      if (['9a', '9b', '9c', '9d', '9e', '9f'].includes(storedTab)) {
        setOpenKeys(['9']);
      }
    }

    const openTab = localStorage.getItem('openTab');
    if (openTab) {
      setOpenKeys([openTab]);
    }

  }, []);

  const handleLogout = () => {

    // onLogout().then(() => {
    //   const path = pathByRouteName('LandingPage', routeConfiguration);

    //   // In production we ensure that data is really lost,
    //   // but in development mode we use stored values for debugging
    //   if (appSettings.dev) {
    //     history.push(path);
    //   } else if (typeof window !== 'undefined') {
    //     window.location = path;
    //   }

    //   console.log('logged out'); // eslint-disable-line
    // });
    onLogout();
  };

  const onMenuClick = values => {
  
    setSelectedTab(values?.key);
    localStorage.setItem('selectedTab', values?.key);

    if (['9a', '9b', '9c', '9d', '9e', '9f'].includes(values?.key)) {
      setOpenKeys(['9']);
      localStorage.setItem('openTab', '9');
    } else {
      setOpenKeys([]);
      localStorage.setItem('openTab', '');
    }

    let page = null;
    switch (values?.key) {
      case '1':
        page = 'DashboardPage';
        break;
      case '2':
        page = null;
        break;
      case '3':
        page = null;
        break;
      case '4':
        page = 'InboxPage';
        break;
      case '5':
        page = 'CalendarPage';
        break;
      case '6':
        page = 'UserManagementPage';
        break;
      case '7':
        page = null;
        break;
      case '11':
        page = 'JobPostsPage';
        break;
      case '12': 
        // page = 'ManageListingsPage/RFPs';
        page = 'RFPPostsPage';
        break;
      case '13': 
        page = 'WalletPage';
        break;
      case '8':
        page = 'ProfileSettingsPage';
        break;
      case '9a':
        page = 'ContactDetailsPage';
        break;
      case '9b':
        page = 'PasswordChangePage';
        break;
      case '9c':
        page = 'StripePayoutPage';
        break;
      case '9d':
        page = 'PaymentMethodsPage';
        break;
      case '9e':
        page = 'TwilioConsentPage';
        break;
      case '9f':
        page = 'PaymentsHistoryPage';
        break;
      case '14':
        page = 'SupportPage';
        break;
      case '10':
        page = 'Logout';
        break;
      default:
        page = null;
    }

    if(page == 'Logout') {
      handleLogout();
    }else if (page) {
      if (page == 'InboxPage') {
        history.push(createResourceLocatorString(page, routeConfiguration, { tab: 'sales' }, {}));
      } else if (page === 'JobPostsPage') {
        history.push(createResourceLocatorString(page, routeConfiguration, { tab: 'job-dashboard' }, {}));
      } else if (page === 'RFPPostsPage') {
        history.push(createResourceLocatorString(page, routeConfiguration, { tab: 'rfp-dashboard' }, {}));
      } else if (page === 'ManageListingsPage/RFPs') {
        page = 'ManageListingsPage'
        history.push(createResourceLocatorString('RFPPostsPage', routeConfiguration, { tab: 'rfp-dashboard' }, {}));
      } else if (page === 'SupportPage') {
        history.push(createResourceLocatorString(page, routeConfiguration, {}));
      } else history.push(createResourceLocatorString(page, routeConfiguration, {}));
    }
  };

  const onMenuOpen = keys => {
    setOpenKeys(keys);
    if (keys.length > 0) {
      setOpenTab(keys[keys.length - 1]);
      localStorage.setItem('openTab', keys[keys.length - 1]);
    }
  };

  const customerType = currentUser?.attributes?.profile?.publicData?.customerProfile?.customerType || profileListing?.attributes?.publicData?.customerProfile?.customerType || null;

  var items = [
    { key: '1', icon: <AppstoreOutlined />, label: 'Dashboard' },
    ...(teamRoleOf(currentUser) === 'Admin' || isCandidate
      ? [{ key: '13', icon: <WalletOutlined />, label: 'iParley Wallet' }]
      : []),
    // ...(isCandidate
    //       ? [{ key: '2', icon: <FileSearchOutlined />, label: 'Find Job' }]
    //       : []),  
    // ...(!isCandidate
    //       ? [{ key: '3', icon: <FileAddOutlined />, label: 'Post Job' }]
    //       : []),
    {
      key: '4',
      icon: <MailOutlined />,
      label: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            Messages
            {(totalUnreadConversations || notificationCount) > 0 && (
              <div style={{
                backgroundColor: '#e74c3c',
                color: 'white',
                borderRadius: '10px',
                minWidth: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: '700',
                padding: '0 5px',
              }}>
                {totalUnreadConversations > 99 ? '99+' : (totalUnreadConversations || notificationCount)}
              </div>
            )}
          </span>
          
        </div>
      ),
    },
    { key: '5', icon: <CalendarOutlined />, label: 'Calendar' },
    ...(!isCandidate && teamRoleOf(currentUser) !== 'Tech Sales'
      ? [{ key: '6', icon: <TeamOutlined />, label: 'Manage Users' }]
      : []),
    // { key: '7', icon: <SafetyOutlined />, label: 'Verifications' },
    ...(isCustomer && customerType?.toLowerCase().includes('implementation') && (teamRoleOf(currentUser) === 'Admin' || teamRoleOf(currentUser) === 'Tech Acquisition' || teamRoleOf(currentUser) === 'Tech Manager')
          ? [{ key: '12', icon: <FileAddOutlined />, label: 'RFP Posts' }]
          : []),
    ...(isCustomer && customerType?.toLowerCase().includes('staffing') && (teamRoleOf(currentUser) === 'Admin' || teamRoleOf(currentUser) === 'HR/Talent Acquisition' || teamRoleOf(currentUser) === 'Hiring Manager')
          ? [{ key: '11', icon: <FileAddOutlined />, label: 'Job Posts' }]
          : []),
    // { key: '11', icon: <PicCenterOutlined />, label: 'Your Listings' },
    { key: '8', icon: <UserOutlined />, label: 'Profile' },
    {
      key: '9',
      icon: <SettingOutlined />,
      label: 'Account Settings',
      children: [
        // { key: '9a', label: 'Contact details' },
        // { key: '9b', label: 'Password' },
        { key: '9c', label: 'Subscription' },
        // { key: '9d', label: 'Message Packs' },
        { key: '9d', label: 'Add-ons' },
        { key: '9e', label: 'SMS Consent' },
        { key: '9f', label: 'Payments History' },
      ],
    },
    { key: '14', icon: <SafetyOutlined />, label: 'Support' },
    { key: '10', icon: <PoweroffOutlined style={{ color: 'red' }} />, label: 'Log Out' },
  ];

  if (isOnboardingForm) {
    //Only show profile, support and logout options in onboarding form
    items = [
      { key: '8', icon: <UserOutlined />, label: 'Profile' },
      { key: '14', icon: <SafetyOutlined />, label: 'Support' },
      { key: '10', icon: <PoweroffOutlined style={{ color: 'red' }} />, label: 'Log Out' },
    ];
  }

  return (
    <div className={css.root}>
      <div className={css.menuAvatar}>        
      <AvatarMedium className={css.avatar} user={currentUser} toProfileSettings={true}/>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
      </div>
      
        
      <div className={css.content}>
        {/* <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton> */}

        {/* {!isOnboardingForm && ( */}
          <div className={css.menuSide}>
            <Menu
              mode="inline"
              items={items}
              onClick={onMenuClick}
              selectedKeys={[selectedTab]}
              openKeys={openKeys}
              onOpenChange={onMenuOpen}
            />
          </div>
        {/* )} */}

        {/* <div className={css.accountLinksWrapper}>
          <NamedLink
            className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
          {!isCandidate && 
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
            </NamedLink>
          }
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
        </div>
        <div className={css.customLinksWrapper}>{extraLinks}</div> */}
        <div className={css.spacer} />
      </div>
      {/* <div className={css.footer}>
        <NamedLink className={css.createNewListingLink} name="NewListingPage">
          <FormattedMessage id="TopbarMobileMenu.newListingLink" />
        </NamedLink>
      </div> */}
    </div>
  );
};

export default TopbarMobileMenu;
